import { useMemo } from 'react'

import { ITypeFieldFilterOptions } from '~/client/graph/types.generated'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import PermitField from '~/client/src/shared/models/PermitField'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import { evaluateFiltersWithLbsContext } from '~/client/src/shared/utils/filterEvaluationHelpers'
import { getLbsRestrictedIdsSetCached } from '~/client/src/shared/utils/lbsRestrictionHelpers'

interface IResult {
  restrictedLocationIds: string[]
  allowedLocationIds: string[]
}

export function useLbsRestrictedIds(
  isLBSEnabled: boolean,
  referenceLocationId: string,
  locationsStore: LocationAttributesStore,
): Set<string> {
  return useMemo(() => {
    if (!isLBSEnabled || !referenceLocationId) {
      return new Set<string>()
    }

    const { hierarchyAllAttributesMap, allAttributes } = locationsStore
    const referenceLocation = hierarchyAllAttributesMap[referenceLocationId]
    if (!referenceLocation) {
      return new Set<string>()
    }

    return getLbsRestrictedIdsSetCached(
      allAttributes,
      referenceLocation,
      hierarchyAllAttributesMap,
    )
  }, [
    isLBSEnabled,
    referenceLocationId,
    locationsStore.allAttributes,
    locationsStore.hierarchyAllAttributesMap,
  ])
}

export function useLocationFiltering(
  isLBSEnabled: boolean,
  isLocationType: boolean,
  isEquipmentOnly: boolean,
  referenceLocationId: string,
  filters: ITypeFieldFilterOptions[],
  locationsStore: LocationAttributesStore,
  workflowFieldsMap?: Record<string, PermitField>,
): IResult {
  if (!isLocationType) {
    return {
      restrictedLocationIds: [],
      allowedLocationIds: [],
    }
  }

  const lbsRestrictedIds = useLbsRestrictedIds(
    isLBSEnabled,
    referenceLocationId,
    locationsStore,
  )

  const allLocations: LocationBase[] = useMemo(() => {
    if (isEquipmentOnly) {
      return locationsStore.offloadingEquipmentsStore.list
    }

    return locationsStore.allLocationsWithoutEquipment
  }, [
    isEquipmentOnly,
    locationsStore.allLocationsWithoutEquipment,
    locationsStore.offloadingEquipmentsStore.list,
  ])

  const { restrictedLocationIds, allowedLocationIds } = useMemo(() => {
    const restricted: string[] = []
    const allowed: string[] = []

    allLocations.forEach(location => {
      const isAllowed = evaluateFiltersWithLbsContext(
        filters,
        location,
        isLBSEnabled,
        lbsRestrictedIds,
        workflowFieldsMap,
      )

      if (isAllowed) {
        allowed.push(location.id)
      } else {
        restricted.push(location.id)
      }
    })

    return {
      restrictedLocationIds: restricted,
      allowedLocationIds: allowed,
    }
  }, [filters, isLBSEnabled, lbsRestrictedIds, allLocations, workflowFieldsMap])

  return useMemo(
    () => ({ restrictedLocationIds, allowedLocationIds }),
    [restrictedLocationIds, allowedLocationIds],
  )
}
